import type { JSX } from 'react'
import type { TvWithPrice } from '~/shared/interfaces'
import type { BannerCardProps } from './index.h'

import { ContentBannerCardTemplate } from "/app/libraries/arcana/src/containers/RowContainer/Templates/index.ts"

import { useUnit } from 'effector-react'
import { memo, useCallback } from 'react'
import { model as router } from '~/core/router'
import {
  getPaymentTvChannelMediaType,
  model as paymentModel,
} from '~/features/payment'
import { paymentTvModelWidget } from '~/widgets/Payment/model'

const isRequiredChannelWithPrice = (
  x: TvWithPrice | undefined
): x is Required<TvWithPrice> => Boolean(x && x.id != null)

export const ChannelBannerCard = memo(ChannelBannerCard_)

export function ChannelBannerCard_(props: BannerCardProps): JSX.Element {
  const selectActiveChannel = useUnit(paymentTvModelWidget.selectActiveChannel)
  const onHowToWatchClick = useUnit(paymentModel.giveItATry)
  const routeHref = useUnit(router.$href)

  const { item, favorites, onSignInClick, onFavoriteClick, onLockedClick } =
    props

  const cardContent = item.content as TvWithPrice | undefined

  const isCardContent = isRequiredChannelWithPrice(cardContent)

  const { id } = isCardContent ? cardContent : { id: undefined }

  const isFavorite =
    id && favorites?.channel && id in favorites.channel
      ? favorites.channel[id]
      : cardContent?.favorite

  const onPlayClickHandler = useCallback(() => {
    if (!isCardContent) return
    // @ts-ignore: TS3693 because TODO fixme FP-3693
    selectActiveChannel({
      ...cardContent,
      categoryId: 0,
      categoryName: 'Banners',
      redirect: routeHref!,
    })
  }, [cardContent, routeHref, selectActiveChannel, isCardContent])

  const onHowToWatchClickHandler = useCallback(() => {
    if (!isCardContent) return

    onHowToWatchClick({
      entity: cardContent,
      mediaType: getPaymentTvChannelMediaType(cardContent.priceSettings),
      link: `/player/live?channelId=${cardContent.id}&categoryId=0&channelNumber=${cardContent.channelNumber}`,
    })
  }, [cardContent, onHowToWatchClick, isCardContent])

  const onSignInClickHandler = useCallback(() => {
    if (!isCardContent) return

    onSignInClick?.({
      name: cardContent.name,
      redirect: routeHref!,
    })
  }, [cardContent, onSignInClick, routeHref, isCardContent])

  const onFavoriteClickHandler = useCallback(() => {
    if (!isCardContent) return

    onFavoriteClick?.({
      type: 'channel',
      id: cardContent.id,
      favorite: Boolean(isFavorite),
      name: cardContent.name,
    })
  }, [cardContent, onFavoriteClick, isFavorite, isCardContent])

  const onLockedClickHandler = useCallback(() => {
    if (!isCardContent) return

    const { id, locked, name } = cardContent

    onLockedClick?.({
      id,
      locked,
      name,
    })
  }, [cardContent, onLockedClick, isCardContent])

  const title = cardContent?.formattedName ?? cardContent?.name

  return (
    <ContentBannerCardTemplate
      {...props}
      title={title}
      logoUrl={cardContent?.logoUrl}
      onPlayClick={onPlayClickHandler}
      onBannerPlayClick={onPlayClickHandler}
      onHowToWatchClick={onHowToWatchClickHandler}
      onSignInClick={onSignInClickHandler}
      onFavoriteClick={onFavoriteClickHandler}
      onLockedClick={onLockedClickHandler}
      isFavorite={isFavorite}
    />
  )
}
